import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { DownloadIcon, DocumentIcon } from "@heroicons/react/outline";
import { downloadData, getUrl } from "aws-amplify/storage";

import Table from "../../../../Components/v2/Table";
import DateTimeCell from "../../../../Components/v2/Table/DateTimeCell";
import Stats from "../../../../Components/v2/Stats";
import AccountingWrapper from "../../../../Components/v2/Accounting/AccountingWrapper";
import AccountingPageHeader from "../../../../Components/v2/Accounting/AccountingPageHeader";
import ReportRevisionNotice from "../../../../Components/v2/Accounting/ReportRevisionNotice";
import ExpandableDescription from "../../../../Components/v2/Accounting/ExpandableDescription";
import Pagination from "../../../../Components/v2/Pagination";
import Modal from "../../../../Components/v2/Modal";
import FormDateRange from "../../../../Components/v2/Form/FormDateRange";
import DepositDetailModal from "../../../../Components/v2/Accounting/DepositDetailModal";
import { isDepositTransaction } from "../../../../Utils/DepositUtil";

import {
  cancelAPIRequest,
  convertToTitleCase,
  getAccountingFileName,
} from "../../../../Utils";
import {
  clearExportGeneralJournal,
  requestExportGeneralJournal,
  requestGeneralJournalSummary,
  requestListAccountingTransaction,
  useAccountingDispatch,
  useAccountingState,
  useAuthState,
} from "../../../../Context";
import { EXPORTS } from "../../../../Constants/accountingReport";
import {
  wibCurrentMonth,
  wibEndOfDay,
  wibStartOfDay,
} from "../../../../Utils/PeriodUtils";
import ErrorDialog from "../../../../Components/v2/ErrorDialog";

const AccountingGeneralJournal = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [dateRange, setDateRange] = useState(wibCurrentMonth);
  const [startDate, endDate] = dateRange;

  // Export State
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const downloadRef = useRef(null);
  const [downloadDocError, setDownloadDocError] = useState(null);
  const [showDownloadErrorModal, setShowDownloadErrorModal] = useState(false);
  const [showExportError, setShowExportError] = useState(false);

  const accountingDispatch = useAccountingDispatch();

  const {
    request,
    journalTransactions,
    journalLoading,
    journalNextTokens,
    journalError,
    generalJournalFile,
    generalJournalFileLoading,
    generalJournalFileError,
    generalJournalSummary,
    generalJournalSummaryLoading,
    generalJournalSummaryError,
    generalJournalSummaryRequest,
  } = useAccountingState();
  // The unmount cleanup only sees the first render's state.
  const summaryRequestRef = useRef(null);
  summaryRequestRef.current = generalJournalSummaryRequest;
  const { user } = useAuthState();
  const [depositDetailID, setDepositDetailID] = useState(null);

  // Clear errors on unmount
  useEffect(() => {
    return () => {
      if (request) {
        cancelAPIRequest(request);
      }
      if (summaryRequestRef.current) {
        cancelAPIRequest(summaryRequestRef.current);
      }
      clearExportGeneralJournal(accountingDispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch data
  useEffect(() => {
    if (!user.mainBusiness?.id) return;
    if ((startDate && !endDate) || (!startDate && endDate)) return;

    const createdAt =
      startDate && endDate
        ? {
            between: [
              wibStartOfDay(startDate),
              wibEndOfDay(endDate),
            ],
          }
        : undefined;

    setPage(1);
    requestListAccountingTransaction(accountingDispatch, {
      page: 1,
      limit: perPage,
      businessID: user.mainBusiness.id,
      createdAt,
      isJournal: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perPage, startDate, endDate, user.mainBusiness?.id]);

  // Period totals: once per range or business, never on a page change.
  const loadSummary = () => {
    if (!user.mainBusiness?.id || !startDate || !endDate) return;
    if (summaryRequestRef.current) {
      cancelAPIRequest(summaryRequestRef.current);
    }
    requestGeneralJournalSummary(accountingDispatch, {
      businessID: user.mainBusiness.id,
      startDate: wibStartOfDay(startDate),
      endDate: wibEndOfDay(endDate),
    });
  };

  useEffect(() => {
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, user.mainBusiness?.id]);

  // Handle Export File Ready
  useEffect(() => {
    if (generalJournalFile?.key) {
      downloadFile(generalJournalFile.key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generalJournalFile]);

  // Handle Export Error
  useEffect(() => {
    if (generalJournalFileError) {
      setShowExportError(true);
    }
  }, [generalJournalFileError]);

  const onPageChange = (selectedPage) => {
    setPage(selectedPage);
    const createdAt =
      startDate && endDate
        ? {
            between: [
              wibStartOfDay(startDate),
              wibEndOfDay(endDate),
            ],
          }
        : undefined;

    if (journalNextTokens[selectedPage] || selectedPage === 1) {
      requestListAccountingTransaction(accountingDispatch, {
        page: selectedPage,
        limit: perPage,
        businessID: user.mainBusiness.id,
        nextToken: journalNextTokens[selectedPage],
        createdAt,
        isJournal: true,
      });
    }
  };

  const onPerPageChange = (itemsPerPage) => {
    setPerPage(itemsPerPage);
  };

  // Export Logic
  const handleExport = useCallback(
    (exportType) => {
      if (!startDate || !endDate) {
        alert(t("accounting.transactions.filter.dateRange"));
        return;
      }
      if (user.mainBusiness?.id) {
        requestExportGeneralJournal(accountingDispatch, {
          businessID: user.mainBusiness.id,
          startDate: wibStartOfDay(startDate),
          endDate: wibEndOfDay(endDate),
          exportType: exportType,
        });
      }
    },
    [accountingDispatch, endDate, startDate, t, user.mainBusiness?.id],
  );

  const onDownloadProgress = (progress) => {
    const { transferredBytes, totalBytes } = progress;
    if (!totalBytes) {
      setDownloadProgress(0);
    } else {
      setDownloadProgress(transferredBytes / totalBytes);
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "download";
    const clickHandler = () => {
      setTimeout(() => {
        URL.revokeObjectURL(url);
        a.removeEventListener("click", clickHandler);
      }, 150);
    };
    a.addEventListener("click", clickHandler, false);
    a.click();
    return a;
  };

  const downloadFile = async (key) => {
    try {
      setDownloading(true);
      const request = downloadData({
        path: ({ identityId }) => `private/${identityId}/${key}`,
        options: {
          onProgress: onDownloadProgress,
        },
      });

      downloadRef.current = request;
      const result = await request.result;
      const fileName = getAccountingFileName(key);

      downloadBlob(result.body, fileName);
    } catch (downloadError) {
      await handleDownloadError(key);
    } finally {
      setDownloading(false);
      setDownloadProgress(0);
      clearExportGeneralJournal(accountingDispatch);
    }
  };

  const handleDownloadError = async (key) => {
    try {
      const urlResult = await getUrl({
        path: ({ identityId }) => `private/${identityId}/${key}`,
        options: {
          useAccelerateEndpoint: true,
        },
      });
      const signedURL = urlResult.url.href;
      window.open(signedURL);
    } catch (signedURLError) {
      setDownloadDocError(signedURLError);
      setShowDownloadErrorModal(true);
    }
  };

  // Transform transactions for the table (Flattening)
  const flattenedTransactions = useMemo(() => {
    if (!journalTransactions) return [];

    const flattened = [];
    journalTransactions.forEach((transaction, transactionIndex) => {
      const items = transaction.accounts?.items || [];
      items.forEach((item, index) => {
        flattened.push({
          id: `${transaction.id}-${index}`, // Unique key
          transactionId: transaction.id,
          transactionIndex: transactionIndex,
          date: transaction.createdAt,
          type: transaction.type,
          // A deposit entry is changed through its deposit; the row links there.
          depositID: isDepositTransaction(transaction)
            ? transaction.depositID
            : null,
          depositNumber: transaction.depositNumber,
          description: transaction.description,
          accountName: item.account?.name,
          debit: item.debit,
          credit: item.credit,
          isFirstItem: index === 0, // Helper to show Date/Type/Desc only on first row
        });
      });
    });
    return flattened;
  }, [journalTransactions]);

  // Without a full range there is no period to total, so the cards stay blank.
  const summaryStats = useMemo(() => {
    const hasSummary =
      startDate && endDate && !generalJournalSummaryError && generalJournalSummary;
    const footer = generalJournalSummaryError
      ? t("reports.generalJournals.summaryError")
      : hasSummary
        ? t("reports.generalJournals.transactionCount", {
            count: generalJournalSummary.transactionCount ?? 0,
          })
        : null;

    return [
      {
        title: t("reports.generalJournals.totalDebit"),
        value: hasSummary ? generalJournalSummary.totalDebit : "–",
        footer,
      },
      {
        title: t("reports.generalJournals.totalCredit"),
        value: hasSummary ? generalJournalSummary.totalCredit : "–",
        footer,
      },
    ];
  }, [
    endDate,
    generalJournalSummary,
    generalJournalSummaryError,
    startDate,
    t,
  ]);

  const exportActionItems = useMemo(() => {
    return EXPORTS.map((item) => ({
      name: t(item.label),
      onClick: () => handleExport(item.value),
    }));
  }, [t, handleExport]);

  return (
    <AccountingWrapper>
      <main className="lg:col-span-9">
        <AccountingPageHeader
          title="General Journal"
          dropDownAction={{
            label: t("Export"),
            icon: <DownloadIcon className="h-5 w-5" />,
            onClick: () => handleExport("pdf"),
            items: exportActionItems,
            disabled: generalJournalFileLoading,
          }}
        />
        <ReportRevisionNotice
          businessID={user.mainBusiness?.id}
          startDate={startDate}
          endDate={endDate}
          className="border-b border-gray-200"
        />

        <div className="px-4 py-5 sm:px-6 lg:px-8">
          <div className="mb-6">
            <Stats
              isLoading={
                Boolean(startDate && endDate) && generalJournalSummaryLoading
              }
              stats={summaryStats}
            />
          </div>

          <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormDateRange
              startDate={startDate}
              endDate={endDate}
              onChange={(update) => {
                setDateRange(update);
              }}
              placeholderText={t("accounting.transactions.filter.dateRange")}
              onClear={() => setDateRange([null, null])}
              disabled={journalLoading}
            />
          </div>

          <Table
            isLoading={journalLoading}
            error={journalError}
            onRetry={() => onPageChange(page)}
            data={flattenedTransactions}
            rowClassName={(row) =>
              row.transactionIndex % 2 === 0 ? "bg-white" : "bg-gray-50"
            }
            columns={[
              {
                header: "Date",
                accessor: (row) =>
                  row.isFirstItem ? <DateTimeCell value={row.date} /> : "",
              },
              {
                header: t("Type & Description"),
                accessor: (row) =>
                  row.isFirstItem ? (
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-900">
                        {t(convertToTitleCase(row.type))}
                      </span>
                      {row.depositID && (
                        <button
                          type="button"
                          onClick={() => setDepositDetailID(row.depositID)}
                          className="self-start text-xs font-medium text-blue-600 hover:underline"
                        >
                          {row.depositNumber || t("deposits.actions.view")}
                        </button>
                      )}
                      {row.description && (
                        <ExpandableDescription
                          text={row.description}
                          maxLength={50}
                        />
                      )}
                    </div>
                  ) : (
                    ""
                  ),
              },
              {
                header: "Account",
                accessor: (row) => t(row.accountName),
              },
              {
                header: "Debit",
                accessor: (row) => (
                  <NumericFormat
                    displayType={"text"}
                    value={row.debit}
                    thousandSeparator={"."}
                    decimalSeparator={","}
                    decimalScale={2}
                    prefix={"Rp"}
                  />
                ),
                align: "right",
              },
              {
                header: "Credit",
                accessor: (row) => (
                  <NumericFormat
                    displayType={"text"}
                    value={row.credit}
                    thousandSeparator={"."}
                    decimalSeparator={","}
                    decimalScale={2}
                    prefix={"Rp"}
                  />
                ),
                align: "right",
              },
            ]}
          />
        </div>

        <Pagination
          nextTokens={journalNextTokens}
          currentPage={page}
          isLoading={journalLoading}
          perPage={perPage}
          onPageChange={onPageChange}
          onPerPageChange={onPerPageChange}
        />

        <Modal
          title="Download File"
          open={downloading}
          onClose={() => {
            if (downloadRef.current) {
              const downloadTask = downloadRef.current;
              downloadTask.cancel();
            }
            setDownloading(false);
          }}
          footer={
            <div className="w-full">
              <p className="text-xs text-slate-500 mb-1">{t("Downloading")}</p>
              <div className="w-full bg-gray-200 rounded-full dark:bg-gray-700">
                <div
                  className="bg-blue-600 text-xs font-medium text-blue-100 text-center p-0.5 leading-none rounded-full transition-all duration-300"
                  style={{
                    width: `${Math.max(4, Math.round(downloadProgress * 100))}%`,
                  }}
                >
                  {Math.round(downloadProgress * 100)}%
                </div>
              </div>
            </div>
          }
        >
          <div className="flex justify-center py-6">
            <DocumentIcon className="h-24 w-24 text-slate-400" />
          </div>
        </Modal>

        {depositDetailID && (
          <DepositDetailModal
            open={Boolean(depositDetailID)}
            depositID={depositDetailID}
            businessID={user?.mainBusiness?.id}
            onClose={() => setDepositDetailID(null)}
            // A refund, forfeit, delete or undo changes the rows on this page
            // and the period totals.
            onChanged={() => {
              onPageChange(page);
              loadSummary();
            }}
          />
        )}

        {/* Error Modals */}
        {downloadDocError && (
          <ErrorDialog
            open={showDownloadErrorModal}
            error={downloadDocError}
            onClose={() => {
              setShowDownloadErrorModal(false);
              setDownloadDocError(null);
            }}
          />
        )}
        {generalJournalFileError && (
          <ErrorDialog
            open={showExportError}
            error={generalJournalFileError}
            onClose={() => {
              setShowExportError(false);
              clearExportGeneralJournal(accountingDispatch);
            }}
          />
        )}
      </main>
    </AccountingWrapper>
  );
};

export default AccountingGeneralJournal;
